import React, { useCallback } from "react";
import { Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./ChallengesSection.scss";
import GhostButton from "../../GhostButton/GhostButton";
import ImageSwords from "../../../../images/swords.svg";
import ChallengeCard from "../../ChallengeCard/ChallengeCard";
import PendingChallengeListModal from "../../modals/PendingChallengeListModal/PendingChallengeListModal";
import ChallengeResponseModal from "../../modals/ChallengeResponseModal/ChallengeResponseModal";
import { useIsChallengeListModalOpen } from "../../../../jotai/atoms/challenges";
import useGetMyPendingChallengesInfiniteQuery from "../../../../react-query/queries/use-get-my-pending-challenges-infinite-query";
import Skeleton from "../../../Skeleton/Skeleton";
import NotificationFlow from "../../../notification-flow/NotificationFlow";
import useNotifications from "../../../../hooks/useNotifications";
import useDisclosure from "../../../../hooks/useDisclosure";
import { useIsChallengeResponseModalOpen } from "../../../../jotai/atoms/challenges";

function LoadingSkeleton() {
  return (
    <div className="px-3 mb-3">
      <Skeleton height={14} width={144} style={{ marginBottom: 22 }} />
      <div className="d-flex justify-content-between">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i}>
            <Skeleton
              className="mb-2"
              height={100}
              width={100}
              borderRadius={12}
            />
            <Skeleton
              className="mb-1"
              height={11}
              width={83}
              borderRadius={15}
            />
            <Skeleton height={11} width={87} borderRadius={15} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ChallengesSection() {
  const { t } = useTranslation("dashboard");
  const { openChallengeListModal } = useIsChallengeListModalOpen();
  const { pendingChallenges, query } = useGetMyPendingChallengesInfiniteQuery({
    limit: 20,
    refetchInterval: 30 * 1000, // 30 seconds
  });
  const { closeChallengeResponseModal } = useIsChallengeResponseModalOpen();
  const notifications = useNotifications();
  const { onOpen: openDownloadApp } = useDisclosure();

  const handleRemindMeLater = useCallback(() => {
    if (!notifications?.notificationsAreEnabled) {
      notifications.openEnableNotificationsDialog();
      closeChallengeResponseModal();
    }
  }, [closeChallengeResponseModal, notifications]);

  if (query.isLoading) return <LoadingSkeleton />;

  if (!pendingChallenges?.length) return null;

  return (
    <section className="challenges-section px-3">
      <div className="heading-container d-flex justify-content-between align-items-center">
        <h2 className="heading d-flex align-items-center gap-1">
          <Image src={ImageSwords} alt="" /> <span>{t("challenges")}</span>
        </h2>
        <GhostButton className="see-all-btn" onClick={openChallengeListModal}>
          {t("see_all")}
        </GhostButton>
      </div>
      <div className="scrollbar-hidden cards-slider mt-3">
        {pendingChallenges?.map((challenge) => (
          <ChallengeCard key={challenge.id} challenge={challenge} />
        ))}
      </div>
      <PendingChallengeListModal
        pendingChallenges={pendingChallenges}
        hasNextPage={query.hasNextPage}
        fetchNextPage={query.fetchNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
      />
      <ChallengeResponseModal onRemindMeLater={handleRemindMeLater} />
      <NotificationFlow handleGetApp={openDownloadApp} {...notifications} />
    </section>
  );
}

export default ChallengesSection;
